import React from 'react';
import { Code, Palette, Globe, Database, Cloud, Activity } from 'lucide-react'; // Changed Zap to Activity for a broader "soft skills/process" icon

const About = () => {
  const skills = [
    {
      category: 'Frontend Development',
      icon: Globe,
      items: ['React.js', 'JavaScript', 'HTML', 'CSS', 'Tailwind CSS', 'Web Design', 'UI/UX Design', 'Progressive Web Applications (PWAs)'],
      color: '#96C7B9'
    },
    {
      category: 'Backend Development',
      icon: Database,
      items: ['Node.js', 'Python', 'Java', 'REST APIs', 'Database Management System (DBMS)', 'MongoDB', 'MySQL', 'Postman API'],
      color: '#7BB5A7'
    },
    {
      category: 'Cloud & DevOps',
      icon: Cloud,
      items: ['Amazon Web Services (AWS)', 'Git', 'GitHub', 'CI/CD', 'Docker'],
      color: '#96C7B9'
    },
    {
      category: 'AI & Emerging Tech',
      icon: Code, // Using code for general tech, could be a robot icon if available
      items: ['Generative AI', 'Prompt Engineering', 'Machine Learning', 'N8n - Agentic Ai', 'Vibe Coding'],
      color: '#7BB5A7'
    },
    {
      category: 'Software Quality & Processes',
      icon: Activity, // Represents testing, troubleshooting, and overall quality
      items: ['Unit Testing', 'Integration Testing', 'Test Automation', 'Troubleshooting', 'Problem Solving', 'Software Development', 'Software Innovation'],
      color: '#96C7B9'
    },
    {
      category: 'Project & Collaboration',
      icon: Palette, // Using Palette for more abstract organizational/communication skills
      items: ['Event Management', 'Project Management', 'Teamwork', 'Communication', 'Organization Skills', 'Business Communications', 'Research Skills'],
      color: '#7BB5A7'
    }
  ];

  // Based on your current experience, 2 months for Synacore and Swamy's Tech Skills.
  // 10 months for Google Developer Groups. 2 months for CODEPLAYERS.
  // Your education period starts Aug 2022. As of Jul 2025, that's roughly 2 years and 11 months of formal education related to tech.
  // Given your earliest relevant experience is Jun 2024, that's 1 year and 1 month of *professional* experience.
  // Let's refine these for a more professional presentation.

  const stats = [
    { number: '8+', label: 'Projects Completed' }, // Based on ERP projects + Student Management System + other events
    { number: '1+', label: 'Years Professional Experience' }, // Sum of relevant work experience durations
    { number: 'Active', label: 'Community Contributor' }, // Highlights GDG and Swamy's Tech Skills Academy
    { number: 'Driven', label: 'Continuous Learner' } // Highlights certifications and broader tech interests
  ];

  return (
    <section
      id="about"
      style={{
        padding: '5rem 0',
        backgroundColor: '#D1F0E0',
        position: 'relative'
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>

        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <p style={{
            color: '#96C7B9',
            fontSize: '0.9rem',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '1rem'
          }}>
            About Me
          </p>
          <h2 style={{
            fontFamily: 'Comfortaa, sans-serif',
            fontSize: 'clamp(2rem, 6vw, 3rem)',
            fontWeight: '600',
            color: '#2C3E50',
            marginBottom: '1rem'
          }}>
            Passionate{' '}
            <span style={{
              background: 'linear-gradient(135deg, #96C7B9 0%, #7BB5A7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Developer
            </span>
          </h2>
          <p style={{
            fontSize: '1.2rem',
            color: '#546C81',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            A highly motivated Full-Stack Developer and Tech Organizer with a strong foundation in building scalable web applications and fostering vibrant tech communities.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4rem' }}>

          {/* About Content */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem', alignItems: 'center' }}>

            {/* Text Content */}
            <div>
              <h3 style={{
                fontFamily: 'Comfortaa, sans-serif',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontWeight: '600',
                color: '#2C3E50',
                marginBottom: '1.5rem'
              }}>
                My Story
              </h3>
              <div style={{ space: '1.5rem' }}>
                <p style={{
                  fontSize: '1rem',
                  color: '#546C81',
                  lineHeight: '1.6',
                  marginBottom: '1.5rem'
                }}>
                  From crafting innovative digital experiences as a founder to organizing impactful tech events, my journey is driven by a passion for technology and community. I thrive on translating complex problems into elegant, user-centric solutions.
                </p>
                <p style={{
                  fontSize: '1rem',
                  color: '#546C81',
                  lineHeight: '1.6',
                  marginBottom: '1.5rem'
                }}>
                  I believe in clean, efficient code and a collaborative approach to development. My commitment to continuous learning keeps me at the forefront of emerging technologies like AI and agentic systems, ensuring I deliver cutting-edge solutions.
                </p>
                <p style={{
                  fontSize: '1rem',
                  color: '#546C81',
                  lineHeight: '1.6'
                }}>
                  My goal is to not only build functional and delightful applications but also to contribute to the growth of the developer ecosystem through active community engagement and knowledge sharing.
                </p>
              </div>
            </div>

            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '2rem',
              backgroundColor: '#FFFFFF',
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(150, 199, 185, 0.1)'
            }}>
              {stats.map((stat, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: '#96C7B9',
                    fontFamily: 'Comfortaa, sans-serif',
                    marginBottom: '0.5rem'
                  }}>
                    {stat.number}
                  </div>
                  <div style={{
                    fontSize: '0.9rem',
                    color: '#546C81',
                    fontWeight: '500'
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Skills Section */}
          <div>
            <h3 style={{
              fontFamily: 'Comfortaa, sans-serif',
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              fontWeight: '600',
              color: '#2C3E50',
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              Skills & Technologies
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem'
            }}>
              {skills.map((skill, index) => {
                const Icon = skill.icon;
                return (
                  <div
                    key={index}
                    style={{
                      backgroundColor: '#FFFFFF',
                      padding: '2rem',
                      borderRadius: '12px',
                      border: `2px solid ${skill.color}20`,
                      boxShadow: '0 4px 15px rgba(150, 199, 185, 0.1)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(150, 199, 185, 0.2)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(150, 199, 185, 0.1)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      marginBottom: '1.5rem'
                    }}>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: `${skill.color}20`,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Icon size={24} style={{ color: skill.color }} />
                      </div>
                      <h4 style={{
                        fontFamily: 'Comfortaa, sans-serif',
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        color: '#2C3E50'
                      }}>
                        {skill.category}
                      </h4>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}>
                      {skill.items.map((item, i) => (
                        <span
                          key={i}
                          style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: `${skill.color}15`,
                            border: `1px solid ${skill.color}30`,
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            color: '#546C81',
                            fontWeight: '500'
                          }}
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;